import React, { useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { 
  Calendar, 
  ChevronLeft, 
  ChevronRight, 
  Clock, 
  User, 
  Phone, 
  Mail,
  CheckCircle,
  X,
  ArrowLeft,
  Car
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { useAPI } from '../hooks/useAPI';
import { bookingsAPI } from '../services/api';

const AdminAgenda = () => {
  const { isAuthenticated } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedBooking, setSelectedBooking] = useState(null);

  // Récupérer les réservations
  const { data: bookings, loading, error, refetch } = useAPI(() => bookingsAPI.getAll());

  // Rediriger si pas connecté
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  // Fonctions de navigation du calendrier
  const goToPrevious = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() - 1);
    setCurrentDate(newDate);
  };

  const goToNext = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + 1);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Générer le calendrier du mois
  const generateCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    const currentDay = new Date(startDate);

    for (let week = 0; week < 6; week++) {
      const weekDays = [];
      for (let day = 0; day < 7; day++) {
        const dayBookings = bookings?.filter(booking => 
          booking.date === currentDay.toISOString().split('T')[0]
        ) || [];

        weekDays.push({
          date: new Date(currentDay),
          bookings: dayBookings,
          isCurrentMonth: currentDay.getMonth() === month,
          isToday: currentDay.toDateString() === new Date().toDateString()
        });

        currentDay.setDate(currentDay.getDate() + 1);
      }
      days.push(weekDays);
    }

    return days;
  };

  const calendarDays = generateCalendarDays();
  const monthNames = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];
  const dayNames = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

  const formatTime = (timeString) => {
    return timeString || 'Heure non spécifiée';
  };

  const getBookingStatusColor = (booking) => {
    // Par défaut, toutes les réservations sont en attente
    return 'bg-orange-100 text-orange-800';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center space-x-4">
              <Link to="/admin/dashboard">
                <Button variant="outline" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Tableau de bord
                </Button>
              </Link>
              <div className="flex items-center space-x-3">
                <Calendar className="w-6 h-6 text-blue-600" />
                <h1 className="text-2xl font-bold text-gray-900">Agenda</h1>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Calendrier */}
          <div className="lg:col-span-3">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-xl">
                    {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                  </CardTitle>
                  <div className="flex items-center space-x-2">
                    <Button onClick={goToPrevious} variant="outline" size="sm">
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button onClick={goToToday} variant="outline" size="sm">
                      Aujourd'hui
                    </Button>
                    <Button onClick={goToNext} variant="outline" size="sm">
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* En-têtes des jours */}
                <div className="grid grid-cols-7 gap-px mb-2">
                  {dayNames.map((day) => (
                    <div key={day} className="p-2 text-center text-sm font-medium text-gray-700">
                      {day}
                    </div>
                  ))}
                </div>

                {/* Grille du calendrier */}
                <div className="grid grid-cols-7 gap-px bg-gray-200 rounded-lg overflow-hidden">
                  {calendarDays.map((week, weekIndex) =>
                    week.map((day, dayIndex) => (
                      <div
                        key={`${weekIndex}-${dayIndex}`}
                        className={`
                          bg-white p-2 min-h-[120px] ${
                            !day.isCurrentMonth ? 'text-gray-400 bg-gray-50' : ''
                          } ${
                            day.isToday ? 'bg-blue-50 ring-2 ring-blue-200' : ''
                          }
                        `}
                      >
                        <div className="flex justify-between items-start mb-1">
                          <span className={`text-sm font-medium ${
                            day.isToday ? 'text-blue-600' : 'text-gray-900'
                          }`}>
                            {day.date.getDate()}
                          </span>
                        </div>

                        {/* Réservations du jour */}
                        <div className="space-y-1">
                          {day.bookings.slice(0, 3).map((booking) => (
                            <button
                              key={booking.id}
                              onClick={() => setSelectedBooking(booking)}
                              className="w-full text-left p-1 rounded text-xs bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors"
                            >
                              <div className="flex items-center space-x-1">
                                <Clock className="w-3 h-3" />
                                <span>{formatTime(booking.time)}</span>
                              </div>
                              <div className="truncate mt-0.5">
                                {booking.client?.firstName} {booking.client?.lastName}
                              </div>
                            </button>
                          ))}
                          {day.bookings.length > 3 && (
                            <div className="text-xs text-gray-500 text-center">
                              +{day.bookings.length - 3} autres
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Liste des réservations */}
          <div>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between text-base">
                  <span>Réservations</span>
                  <Badge variant="secondary">{bookings?.length || 0}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-4">Chargement...</div>
                ) : bookings && bookings.length > 0 ? (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {bookings.map((booking) => (
                      <div
                        key={booking.id}
                        onClick={() => setSelectedBooking(booking)}
                        className="p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-medium text-sm">
                              {booking.client?.firstName} {booking.client?.lastName}
                            </p>
                            <p className="text-xs text-gray-500">
                              {booking.date} à {formatTime(booking.time)}
                            </p>
                          </div>
                          <Badge className={getBookingStatusColor(booking)} variant="secondary">
                            En attente
                          </Badge>
                        </div>
                        {booking.client?.phone && (
                          <p className="text-xs text-gray-600">
                            📞 {booking.client.phone}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-gray-500">
                    <Calendar className="w-12 h-12 mx-auto text-gray-300 mb-2" />
                    <p className="text-sm">Aucune réservation</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Modal détails réservation */}
      {selectedBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <div className="flex justify-between items-start">
                <CardTitle>Détails de la réservation</CardTitle>
                <Button
                  onClick={() => setSelectedBooking(null)}
                  variant="ghost"
                  size="sm"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Client</h4>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <User className="w-4 h-4 text-gray-400" />
                    <span>{selectedBooking.client?.firstName} {selectedBooking.client?.lastName}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <span>{selectedBooking.client?.email}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <span>{selectedBooking.client?.phone}</span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-2">Rendez-vous</h4>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span>{selectedBooking.date}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Clock className="w-4 h-4 text-gray-400" />
                    <span>{formatTime(selectedBooking.time)}</span>
                  </div>
                </div>
              </div>

              {selectedBooking.vehicle && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Véhicule</h4>
                  <div className="flex items-center space-x-2">
                    <Car className="w-4 h-4 text-gray-400" />
                    <span>
                      {selectedBooking.vehicle.brand} {selectedBooking.vehicle.model} {selectedBooking.vehicle.year}
                    </span>
                  </div>
                </div>
              )}

              {selectedBooking.notes && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Notes</h4>
                  <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
                    {selectedBooking.notes}
                  </p>
                </div>
              )}

              <div className="flex space-x-2 pt-4">
                <Button className="flex-1 bg-green-600 hover:bg-green-700">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Confirmer
                </Button>
                <Button variant="outline" className="flex-1">
                  <Phone className="w-4 h-4 mr-2" />
                  Appeler
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminAgenda;